import React from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ListGroup,
} from 'react-bootstrap';
import PageHeader from '../../common/PageHeader';
import './Experience.css';

const DesignAndArchitecture = () => (
  <>
    <PageHeader title="Design & Architecture" />
    <div className="experience-section design-page" style={{ 
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      minHeight: '100vh'
    }}>
      <Container className="pt-4 pb-4">
      <hr className="mb-4"/>
    
    <Row className="mb-4">
      <Col md={12}>
        <p className="lead text-muted">
          Passionate about creating scalable, maintainable, and robust software architectures. 
          My experience spans across system design, architectural patterns, AI/ML applications, 
          large-scale distributed systems, and technical leadership in building enterprise-grade applications.
        </p>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={6}>
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">System Architecture</h5>
          </Card.Header>
          <Card.Body>
            <ListGroup variant="flush">
              <ListGroup.Item className="border-0 px-0">
                <strong>Microservices Architecture:</strong> Designed and implemented distributed systems with service-oriented architecture
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Event-Driven Architecture:</strong> Built scalable systems using message queues and event streaming
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>API Design:</strong> RESTful and GraphQL API architecture with proper versioning strategies
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Cloud Architecture:</strong> AWS/Azure cloud-native solutions with serverless and containerized deployments
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Distributed Systems:</strong> Large-scale systems with consensus algorithms, sharding, and eventual consistency
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>AI/ML Infrastructure:</strong> Scalable ML pipelines, model serving, and real-time inference systems
              </ListGroup.Item>
            </ListGroup>
          </Card.Body>
        </Card>
      </Col>
      
      <Col md={6}>
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-success text-white">
            <h5 className="mb-0">Design Patterns & Principles</h5>
          </Card.Header>
          <Card.Body>
            <ListGroup variant="flush">
              <ListGroup.Item className="border-0 px-0">
                <strong>SOLID Principles:</strong> Applied object-oriented design principles for maintainable code
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Domain-Driven Design:</strong> Implemented DDD patterns for complex business domains
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Design Patterns:</strong> Factory, Observer, Strategy, Repository, and CQRS patterns
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Clean Architecture:</strong> Layered architecture with separation of concerns
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>CAP Theorem:</strong> Designing distributed systems with consistency, availability, and partition tolerance trade-offs
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>ML Design Patterns:</strong> Feature stores, model registry, A/B testing frameworks, and MLOps patterns
              </ListGroup.Item>
            </ListGroup>
          </Card.Body>
        </Card>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={12}>
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-info text-white">
            <h5 className="mb-0">Key Architecture Projects</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <h6 className="text-primary">Enterprise Application Platform</h6>
                <p className="mb-3">
                  Led the architectural design of a scalable enterprise platform serving thousands of users. 
                  Implemented microservices architecture with event sourcing, achieving 99.9% uptime and 
                  reducing response times by 40%.
                </p>
                <div className="mb-3">
                  <Badge variant="outline-primary" className="mr-2">Microservices</Badge>
                  <Badge variant="outline-primary" className="mr-2">Event Sourcing</Badge>
                  <Badge variant="outline-primary" className="mr-2">CQRS</Badge>
                </div>
              </Col>
              <Col md={4}>
                <h6 className="text-primary">Real-time Data Processing System</h6>
                <p className="mb-3">
                  Architected a high-throughput data processing pipeline capable of handling millions 
                  of events per day. Designed fault-tolerant systems with automatic failover and 
                  horizontal scaling capabilities.
                </p>
                <div className="mb-3">
                  <Badge variant="outline-success" className="mr-2">Apache Kafka</Badge>
                  <Badge variant="outline-success" className="mr-2">Stream Processing</Badge>
                  <Badge variant="outline-success" className="mr-2">Auto Scaling</Badge>
                </div>
              </Col>
              <Col md={4}>
                <h6 className="text-primary">AI-Powered Recommendation Engine</h6>
                <p className="mb-3">
                  Designed and implemented a distributed machine learning system for real-time 
                  personalization. Built scalable model serving infrastructure handling 10M+ 
                  predictions per day with sub-100ms latency.
                </p>
                <div className="mb-3">
                  <Badge variant="outline-info" className="mr-2">TensorFlow Serving</Badge>
                  <Badge variant="outline-info" className="mr-2">Feature Store</Badge>
                  <Badge variant="outline-info" className="mr-2">A/B Testing</Badge>
                </div>
              </Col>
            </Row>
            
            <Row className="mt-4">
              <Col md={6}>
                <h6 className="text-success">Global Content Delivery Network</h6>
                <p className="mb-3">
                  Architected a globally distributed CDN system with edge computing capabilities. 
                  Implemented intelligent routing algorithms and dynamic content caching, 
                  reducing global latency by 65% across 50+ geographic regions.
                </p>
                <div className="mb-3">
                  <Badge variant="outline-warning" className="mr-2">Edge Computing</Badge>
                  <Badge variant="outline-warning" className="mr-2">Global Load Balancing</Badge>
                  <Badge variant="outline-warning" className="mr-2">GeoDNS</Badge>
                </div>
              </Col>
              <Col md={6}>
                <h6 className="text-success">Distributed ML Training Platform</h6>
                <p className="mb-3">
                  Built a multi-tenant platform for distributed machine learning model training. 
                  Designed resource allocation algorithms and implemented fault-tolerant training 
                  workflows supporting petabyte-scale datasets.
                </p>
                <div className="mb-3">
                  <Badge variant="outline-dark" className="mr-2">Kubernetes</Badge>
                  <Badge variant="outline-dark" className="mr-2">Distributed Training</Badge>
                  <Badge variant="outline-dark" className="mr-2">Resource Orchestration</Badge>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={6}>
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-gradient" style={{background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', color: 'white'}}>
            <h5 className="mb-0">AI/ML Architecture & Applications</h5>
          </Card.Header>
          <Card.Body>
            <ListGroup variant="flush">
              <ListGroup.Item className="border-0 px-0">
                <strong>MLOps Pipelines:</strong> End-to-end ML workflows with CI/CD, automated model training and deployment
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Real-time Inference:</strong> Low-latency model serving with auto-scaling and A/B testing frameworks
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Feature Engineering:</strong> Distributed feature stores, real-time feature computation, and data versioning
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Model Monitoring:</strong> Drift detection, performance tracking, and automated retraining systems
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>AI Applications:</strong> NLP systems, computer vision pipelines, and recommendation engines
              </ListGroup.Item>
            </ListGroup>
          </Card.Body>
        </Card>
      </Col>
      
      <Col md={6}>
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-gradient" style={{background: 'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)', color: 'white'}}>
            <h5 className="mb-0">Large-Scale Distributed Systems</h5>
          </Card.Header>
          <Card.Body>
            <ListGroup variant="flush">
              <ListGroup.Item className="border-0 px-0">
                <strong>Consensus Algorithms:</strong> Raft, Paxos implementations for distributed coordination and leader election
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Data Partitioning:</strong> Horizontal sharding, consistent hashing, and distributed data management
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Fault Tolerance:</strong> Circuit breakers, bulkhead patterns, and graceful degradation strategies
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Global Scale:</strong> Multi-region deployments, eventual consistency, and conflict resolution
              </ListGroup.Item>
              <ListGroup.Item className="border-0 px-0">
                <strong>Stream Processing:</strong> Real-time data processing with Apache Kafka, Flink, and Storm
              </ListGroup.Item>
            </ListGroup>
          </Card.Body>
        </Card>
      </Col>
    </Row>

    <Row className="mb-4">
      <Col md={4}>
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-warning text-dark">
            <h6 className="mb-0">Database Design</h6>
          </Card.Header>
          <Card.Body>
            <ul className="list-unstyled mb-0">
              <li>• Relational database modeling</li>
              <li>• NoSQL schema design</li>
              <li>• Data partitioning strategies</li>
              <li>• Performance optimization</li>
              <li>• ACID compliance</li>
            </ul>
          </Card.Body>
        </Card>
      </Col>
      
      <Col md={4}>
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-secondary text-white">
            <h6 className="mb-0">Security Architecture</h6>
          </Card.Header>
          <Card.Body>
            <ul className="list-unstyled mb-0">
              <li>• OAuth 2.0 / JWT implementation</li>
              <li>• API security best practices</li>
              <li>• Data encryption strategies</li>
              <li>• Vulnerability assessments</li>
              <li>• Zero-trust architecture</li>
            </ul>
          </Card.Body>
        </Card>
      </Col>
      
      <Col md={4}>
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-dark text-white">
            <h6 className="mb-0">Performance & Scalability</h6>
          </Card.Header>
          <Card.Body>
            <ul className="list-unstyled mb-0">
              <li>• Load balancing strategies</li>
              <li>• Caching mechanisms</li>
              <li>• CDN optimization</li>
              <li>• Database query optimization</li>
              <li>• Horizontal scaling patterns</li>
            </ul>
          </Card.Body>
        </Card>
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-danger text-white">
            <h5 className="mb-0">Technical Leadership & Architecture Decisions</h5>
          </Card.Header>
          <Card.Body>
            <p>
              As a technical leader, I've guided teams through critical architectural decisions, 
              technology stack selections, system migrations, and AI/ML transformations. My approach emphasizes:
            </p>
            <Row>
              <Col md={6}>
                <ul>
                  <li><strong>Collaborative Design:</strong> Facilitating architecture review sessions and technical discussions</li>
                  <li><strong>Documentation:</strong> Creating comprehensive architectural documentation and decision records</li>
                  <li><strong>Risk Assessment:</strong> Evaluating technical debt and planning strategic refactoring initiatives</li>
                  <li><strong>ML Strategy:</strong> Leading AI/ML adoption roadmaps and MLOps implementation</li>
                </ul>
              </Col>
              <Col md={6}>
                <ul>
                  <li><strong>Technology Evaluation:</strong> Assessing new technologies, ML frameworks, and distributed systems tools</li>
                  <li><strong>Mentoring:</strong> Teaching architectural concepts, distributed systems principles, and ML best practices</li>
                  <li><strong>Innovation:</strong> Driving adoption of modern patterns, AI applications, and cloud-native architectures</li>
                  <li><strong>Scale Planning:</strong> Designing systems for global scale, high availability, and fault tolerance</li>
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  </Container>
  </div>
  </>
);

export default DesignAndArchitecture;